import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, useInView, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  CheckCircle2, Printer, Globe, Rocket, Megaphone, 
  Menu, X, ChevronDown, ArrowRight, Sparkles,
  FileText, Palette, Target, Users, Star, Zap,
  Building2, Hammer, Leaf, Wrench, ChevronRight
} from 'lucide-react';
import { usePageMeta } from '../hooks/usePageMeta';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 60 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.8 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5, ease: "easeOut" } }
};

// Animated Counter Component
const AnimatedCounter = ({ value, suffix = "", duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  useEffect(() => {
    if (isInView) {
      const end = parseInt(value);
      // Handle zero value
      if (end === 0) {
        setCount(0);
        return;
      }
      let start = 0;
      const incrementTime = (duration * 1000) / end;
      const timer = setInterval(() => {
        start += 1;
        setCount(start);
        if (start >= end) clearInterval(timer);
      }, incrementTime);
      return () => clearInterval(timer);
    }
  }, [isInView, value, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

// Floating Animation Component
const FloatingElement = ({ children, delay = 0, duration = 3 }) => (
  <motion.div
    animate={{ y: [0, -10, 0] }}
    transition={{ duration, repeat: Infinity, delay, ease: "easeInOut" }}
  >
    {children}
  </motion.div>
);

// Marquee Component for logos
const Marquee = ({ children, direction = "left", speed = 30 }) => {
  return (
    <div className="overflow-hidden whitespace-nowrap">
      <motion.div
        className="inline-flex"
        animate={{ x: direction === "left" ? ["0%", "-50%"] : ["-50%", "0%"] }}
        transition={{ duration: speed, repeat: Infinity, ease: "linear" }}
      >
        {children}
        {children}
      </motion.div>
    </div>
  );
};

// Animated Text Reveal
const TextReveal = ({ children, delay = 0 }) => {
  return (
    <motion.span
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay, ease: "easeOut" }}
      className="inline-block"
    >
      {children}
    </motion.span>
  );
};

// Service Card with 3D hover effect
const ServiceCard = ({ icon: Icon, title, description, color, delay }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.div
      variants={fadeInUp}
      whileHover={{ y: -8, scale: 1.02 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="relative bg-white rounded-2xl p-6 shadow-lg border border-gray-100 overflow-hidden group cursor-pointer"
      style={{ transformStyle: "preserve-3d" }}
    >
      {/* Background gradient on hover */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)` }}
      />
      
      {/* Animated corner accent */}
      <motion.div
        className="absolute -top-10 -right-10 w-20 h-20 rounded-full"
        style={{ background: color }}
        animate={{ scale: isHovered ? 1.5 : 1, opacity: isHovered ? 0.1 : 0.05 }}
        transition={{ duration: 0.3 }}
      />
      
      <div className="relative z-10">
        <motion.div
          className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
          style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}CC 100%)` }}
          whileHover={{ rotate: [0, -10, 10, 0] }}
          transition={{ duration: 0.5 }}
        >
          <Icon className="w-7 h-7 text-white" />
        </motion.div>
        
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-600 leading-relaxed">{description}</p>
        
        <motion.div
          className="mt-4 flex items-center gap-2 font-medium"
          style={{ color }}
          initial={{ x: 0 }}
          animate={{ x: isHovered ? 5 : 0 }}
        >
          Learn more <ArrowRight className="w-4 h-4" />
        </motion.div>
      </div>
    </motion.div>
  );
};

// Industry Badge Component
const IndustryBadge = ({ icon: Icon, label, delay }) => (
  <motion.div
    initial={{ opacity: 0, scale: 0.8 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ delay, duration: 0.4 }}
    whileHover={{ scale: 1.05, y: -2 }}
    className="flex items-center gap-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-md border border-gray-100"
  >
    <Icon className="w-4 h-4 text-[#1E3A8A]" />
    <span className="text-sm font-medium text-gray-700">{label}</span>
  </motion.div>
);

// Pricing Card Component
const PricingCard = ({ plan, featured, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -10 }}
      className={`relative rounded-2xl p-8 ${
        featured 
          ? 'bg-gradient-to-br from-[#1E3A8A] to-[#3B82F6] text-white shadow-2xl scale-105' 
          : 'bg-white border-2 border-gray-100 shadow-lg'
      }`}
    >
      {featured && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="absolute -top-4 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 px-4 py-1 rounded-full text-sm font-bold"
        >
          Most Popular
        </motion.div>
      )}
      
      <h3 className={`text-2xl font-bold mb-2 ${featured ? 'text-white' : 'text-gray-900'}`}>
        {plan.title}
      </h3>
      <div className="mb-4">
        <span className={`text-4xl font-bold ${featured ? 'text-white' : 'text-[#1E3A8A]'}`}>
          {plan.price}
        </span>
      </div>
      <p className={`mb-6 ${featured ? 'text-blue-100' : 'text-gray-600'}`}>
        {plan.description}
      </p>
      
      <ul className="space-y-3 mb-8">
        {plan.features.map((feature, idx) => (
          <motion.li
            key={idx}
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 + idx * 0.1 }}
            className="flex items-start gap-3"
          >
            <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${featured ? 'text-amber-300' : 'text-green-500'}`} />
            <span className={featured ? 'text-blue-50' : 'text-gray-700'}>{feature}</span>
          </motion.li>
        ))}
      </ul>
      
      <Link to="/request">
        <Button 
          className={`w-full py-6 text-lg font-semibold transition-all duration-300 ${
            featured 
              ? 'bg-white text-[#1E3A8A] hover:bg-gray-100' 
              : 'bg-[#1E3A8A] text-white hover:bg-[#1E3A8A]/90'
          }`}
        >
          {plan.cta}
        </Button>
      </Link>
    </motion.div>
  );
};

// Stats Component
const StatItem = ({ number, suffix, label, delay }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay }}
      className="text-center"
    >
      <div className="text-4xl md:text-5xl font-bold text-[#1E3A8A] mb-2">
        <AnimatedCounter value={number} suffix={suffix} />
      </div>
      <p className="text-gray-600 font-medium">{label}</p>
    </motion.div>
  );
};

const Landing = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroRef = useRef(null);
  
  // Parallax effects
  const heroY = useTransform(scrollY, [0, 500], [0, 150]);
  const heroOpacity = useTransform(scrollY, [0, 300], [1, 0]);
  
  usePageMeta({
    title: 'BM-Assist.co | Marketing Launchpad for Local Businesses',
    description: 'Get your local business started with professional marketing materials. Business profiles, flyers, business cards, and ad setup for contractors, home services, and trades.',
    ogTitle: 'BM-Assist.co | Marketing Launchpad for Local Businesses',
    ogDescription: 'One-time marketing projects for construction, lawn care, and home service businesses. Get started professionally without ongoing retainers.'
  });

  const services = [
    {
      icon: Globe,
      title: "Business Profile Setup",
      description: "Get your Google Business Profile, Yelp, and social media pages set up professionally. We create them, you own them.",
      color: "#1E3A8A"
    },
    {
      icon: Printer,
      title: "Print Materials",
      description: "Professional business cards, flyers, door hangers, and yard signs designed and ready for print.",
      color: "#F59E0B"
    },
    {
      icon: Rocket,
      title: "Ad Launch Setup",
      description: "We'll set up your first Facebook or Google ad campaign. You take it from there with full control.",
      color: "#10B981"
    },
    {
      icon: Palette,
      title: "Brand Starter Kit",
      description: "Logo concepts, color palette, and brand guidelines to give your business a polished, consistent look.",
      color: "#8B5CF6"
    }
  ];

  const pricingPlans = [
    {
      title: "Digital Setup",
      price: "$299",
      description: "Get found online by local customers",
      features: [
        "Google Business Profile setup",
        "Facebook Business Page creation",
        "Basic SEO optimization",
        "Review response templates"
      ],
      cta: "Get Started"
    },
    {
      title: "Local Ad Boost",
      price: "$499",
      description: "Start getting leads with ads",
      features: [
        "2-week ad campaign",
        "Custom ad creative & copy",
        "$100 ad spend included",
        "Performance reporting"
      ],
      cta: "Most Popular"
    },
    {
      title: "Offline Essentials",
      price: "$249",
      description: "Professional print materials",
      features: [
        "Business card design",
        "Flyer or door hanger design",
        "Print-ready files",
        "2 revision rounds"
      ],
      cta: "Get Started"
    },
    {
      title: "Promo & Social",
      price: "$399",
      description: "Content that converts",
      features: [
        "5 social media templates",
        "FB & IG cover graphics",
        "Editable Canva files",
        "Brand color palette"
      ],
      cta: "Get Started"
    }
  ];

  const industries = [
    { icon: Hammer, label: "Contractors" },
    { icon: Building2, label: "Construction" },
    { icon: Leaf, label: "Lawn Care" },
    { icon: Wrench, label: "Home Services" }
  ];

  const [pricingDropdownOpen, setPricingDropdownOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav 
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 w-full bg-white/90 backdrop-blur-md border-b border-gray-100 z-50"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <motion.div 
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
            >
              <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] bg-clip-text text-transparent">
                BM-Assist.co
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              {/* Services */}
              <motion.div className="relative group">
                <button
                  onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-gray-600 hover:text-[#1E3A8A] transition-colors duration-200 font-medium"
                >
                  Services
                </button>
                <motion.div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#1E3A8A] group-hover:w-full transition-all duration-300" />
              </motion.div>

              {/* Pricing Dropdown */}
              <div 
                className="relative"
                onMouseEnter={() => setPricingDropdownOpen(true)}
                onMouseLeave={() => setPricingDropdownOpen(false)}
              >
                <button className="text-gray-600 hover:text-[#1E3A8A] transition-colors duration-200 font-medium flex items-center gap-1">
                  Pricing
                  <ChevronDown className={`w-4 h-4 transition-transform ${pricingDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
                <AnimatePresence>
                  {pricingDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden"
                    >
                      <Link 
                        to="/bundles"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <div className="w-8 h-8 bg-[#1E3A8A]/10 rounded-lg flex items-center justify-center">
                          <Rocket className="w-4 h-4 text-[#1E3A8A]" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Launch Bundles</div>
                          <div className="text-xs text-gray-500">Complete packages</div>
                        </div>
                      </Link>
                      <Link 
                        to="/quick-fixes"
                        className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors border-t border-gray-100"
                      >
                        <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center">
                          <Zap className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">Quick Fixes</div>
                          <div className="text-xs text-gray-500">Individual services</div>
                        </div>
                      </Link>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Our Team */}
              <motion.div className="relative group">
                <Link to="/team" className="text-gray-600 hover:text-[#1E3A8A] transition-colors duration-200 font-medium">
                  Our Team
                </Link>
                <motion.div className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#1E3A8A] group-hover:w-full transition-all duration-300" />
              </motion.div>
              
              <Link to="/request">
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-6 py-2 rounded-full shadow-lg shadow-blue-500/25">
                    Start a Project
                    <Sparkles className="w-4 h-4 ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>

            {/* Mobile menu button */}
            <motion.button
              whileTap={{ scale: 0.9 }}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </motion.button>
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden bg-white border-t border-gray-100"
            >
              <div className="px-4 py-4 space-y-3">
                <button
                  onClick={() => { document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' }); setMobileMenuOpen(false); }}
                  className="block w-full text-left px-4 py-2 rounded-lg hover:bg-gray-50 text-gray-700 font-medium"
                >
                  Services
                </button>
                <Link to="/bundles" className="block px-4 py-2 rounded-lg hover:bg-gray-50 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Launch Bundles
                </Link>
                <Link to="/quick-fixes" className="block px-4 py-2 rounded-lg hover:bg-gray-50 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Quick Fixes
                </Link>
                <Link to="/team" className="block px-4 py-2 rounded-lg hover:bg-gray-50 text-gray-700 font-medium" onClick={() => setMobileMenuOpen(false)}>
                  Our Team
                </Link>
                <Link to="/request" className="block" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#1E3A8A] text-white">Start a Project</Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Hero Section */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        {/* Animated background elements - pushed further back with lower opacity */}
        <div className="absolute inset-0 overflow-hidden -z-10 pointer-events-none">
          <motion.div
            className="absolute top-20 left-10 w-72 h-72 bg-blue-200 rounded-full filter blur-[100px] opacity-20"
            animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-20 right-10 w-96 h-96 bg-amber-200 rounded-full filter blur-[100px] opacity-15"
            animate={{ x: [0, -40, 0], y: [0, 30, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-purple-200 rounded-full filter blur-[100px] opacity-15"
            animate={{ scale: [1, 1.2, 1] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="relative z-20 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center"
        >
          {/* Main headline - MOBILE OPTIMIZED */}
          <motion.h1 
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <span className="text-gray-900">Marketing </span>
            <span className="relative inline-block">
              <span className="bg-gradient-to-r from-[#1E3A8A] via-[#3B82F6] to-[#1E3A8A] bg-clip-text text-transparent">
                made simple
              </span>
              <motion.svg
                className="absolute -bottom-1 sm:-bottom-2 left-0 w-full"
                viewBox="0 0 300 12"
                initial={{ pathLength: 0 }}
                animate={{ pathLength: 1 }}
                transition={{ duration: 1, delay: 0.8 }}
              >
                <motion.path
                  d="M0 6 Q75 0, 150 6 T300 6"
                  fill="none"
                  stroke="#F59E0B"
                  strokeWidth="4"
                  strokeLinecap="round"
                />
              </motion.svg>
            </span>
          </motion.h1>

          {/* Subheadline for local trades */}
          <motion.p
            className="text-xl sm:text-2xl md:text-3xl font-semibold text-[#1E3A8A] mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            for contractors & home services
          </motion.p>

          {/* Industry badges - Below headline on mobile */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-6 sm:mb-8"
          >
            {industries.map((industry, index) => (
              <IndustryBadge key={industry.label} {...industry} delay={0.6 + index * 0.1} />
            ))}
          </motion.div>

          {/* Subheadline */}
          <motion.p 
            className="text-base sm:text-xl md:text-2xl text-gray-600 max-w-3xl mx-auto mb-8 sm:mb-10 leading-relaxed px-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
          >
            We help <span className="text-[#1E3A8A] font-semibold">contractors, lawn care pros,</span> and{' '}
            <span className="text-[#1E3A8A] font-semibold">home service businesses</span> get started with 
            professional marketing — without the agency retainer.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
          >
            <Link to="/request">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white text-lg px-8 py-6 rounded-full shadow-xl shadow-blue-500/30 group">
                  Start Your Project
                  <motion.span
                    className="ml-2 inline-block"
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRight className="w-5 h-5" />
                  </motion.span>
                </Button>
              </motion.div>
            </Link>
            
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-[#1E3A8A] font-semibold text-lg flex items-center gap-2 hover:gap-3 transition-all"
            >
              See what we do
              <ChevronDown className="w-5 h-5" />
            </motion.button>
          </motion.div>

          {/* Floating decorative elements */}
          <div className="absolute -z-10 inset-0 pointer-events-none">
            <FloatingElement delay={0}>
              <motion.div className="absolute top-10 right-20 text-4xl" initial={{ rotate: 0 }} animate={{ rotate: 360 }} transition={{ duration: 20, repeat: Infinity, ease: "linear" }}>
                ⚡
              </motion.div>
            </FloatingElement>
            <FloatingElement delay={1}>
              <div className="absolute bottom-20 left-20 text-3xl">🎯</div>
            </FloatingElement>
            <FloatingElement delay={0.5}>
              <div className="absolute top-1/3 left-10 text-2xl">✨</div>
            </FloatingElement>
          </div>
        </motion.div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-gray-300 rounded-full flex justify-center pt-2">
            <motion.div
              className="w-1.5 h-3 bg-[#1E3A8A] rounded-full"
              animate={{ y: [0, 12, 0], opacity: [1, 0.5, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatItem number="127" suffix="+" label="Projects Delivered" delay={0} />
            <StatItem number="96" suffix="%" label="Client Satisfaction" delay={0.1} />
            <StatItem number="48" suffix="hr" label="Avg. Turnaround" delay={0.2} />
            <StatItem number="0" suffix="" label="Monthly Retainers" delay={0.3} />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-[#1E3A8A]/10 text-[#1E3A8A] rounded-full text-sm font-semibold mb-4"
            >
              What We Do
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
            >
              Get started. <span className="text-[#1E3A8A]">We handle the setup.</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-2xl mx-auto"
            >
              One-time projects to launch your marketing. No monthly fees, no long-term contracts.
              You get the deliverables, you own them forever.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {services.map((service, index) => (
              <ServiceCard key={service.title} {...service} delay={index * 0.1} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-24 bg-[#1E3A8A] relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-white mb-4"
            >
              How it works
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-blue-100 max-w-2xl mx-auto"
            >
              Three simple steps to professional marketing materials
            </motion.p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Tell us what you need", description: "Fill out a quick form about your business and what marketing materials you're looking for. Takes just 2 minutes.", icon: FileText },
              { step: "02", title: "We create it", description: "Our team designs and sets up everything. You review and request changes until it's exactly right.", icon: Palette },
              { step: "03", title: "You own it", description: "Get all your files and access credentials. It's yours forever — no strings attached, no monthly fees.", icon: CheckCircle2 }
            ].map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="relative h-full"
              >
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20 hover:bg-white/20 transition-colors h-full flex flex-col">
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 5 }}
                    className="w-16 h-16 bg-amber-400 rounded-xl flex items-center justify-center mb-6"
                  >
                    <item.icon className="w-8 h-8 text-[#1E3A8A]" />
                  </motion.div>
                  <span className="text-amber-400 font-bold text-sm mb-2 block">STEP {item.step}</span>
                  <h3 className="text-2xl font-bold text-white mb-3">{item.title}</h3>
                  <p className="text-blue-100 leading-relaxed flex-grow">{item.description}</p>
                </div>
                
                {/* Connector line */}
                {index < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 border-t-2 border-dashed border-white/30" />
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Launch Bundles Section */}
      <section id="bundles" className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-16"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block px-4 py-2 bg-amber-100 text-amber-700 rounded-full text-sm font-semibold mb-4"
            >
              <Rocket className="w-4 h-4 inline mr-1" /> Launch Bundles
            </motion.span>
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
            >
              Pick what you <span className="text-[#1E3A8A]">need</span>
            </motion.h2>
            <motion.p 
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-2xl mx-auto"
            >
              Focused bundles that solve specific problems. One price, no monthly fees.
            </motion.p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
            {pricingPlans.map((plan, index) => (
              <PricingCard key={plan.title} plan={plan} featured={index === 1} index={index} />
            ))}
          </div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mt-12"
          >
            <Link to="/bundles">
              <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A] hover:bg-[#1E3A8A] hover:text-white rounded-full px-8 py-6">
                Compare All Bundles <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Quick Fixes CTA Section */}
      <section className="py-16 bg-gradient-to-r from-amber-50 to-amber-100/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-between gap-8"
          >
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-2 mb-3">
                <div className="w-10 h-10 bg-amber-400 rounded-full flex items-center justify-center">
                  <Zap className="w-5 h-5 text-amber-900" />
                </div>
                <span className="text-amber-700 font-semibold">Quick Fixes</span>
              </div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                Just need one thing?
              </h3>
              <p className="text-gray-600 max-w-md">
                Get individual services like business cards, yard signs, or social graphics — starting at just $39.
              </p>
            </div>
            <Link to="/quick-fixes">
              <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                <Button className="bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white px-8 py-6 rounded-full shadow-lg text-lg font-semibold whitespace-nowrap">
                  Browse Quick Fixes <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="text-center mb-12"
          >
            <motion.div variants={fadeInUp} className="flex items-center justify-center gap-2 mb-4">
              {[1, 2, 3, 4, 5].map((_, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * i }}
                >
                  <Star className={`w-8 h-8 ${i < 5 ? 'text-amber-400 fill-amber-400' : 'text-amber-400'}`} />
                </motion.div>
              ))}
              <span className="text-2xl font-bold text-gray-900 ml-2">4.9</span>
            </motion.div>
            <motion.h2 
              variants={fadeInUp}
              className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4"
            >
              Trusted by <span className="text-[#1E3A8A]">local businesses</span>
            </motion.h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              { 
                name: "Mike R.", 
                business: "M&R Landscaping", 
                text: "Got my Google Business set up and business cards designed in 2 days. Already getting calls from the profile. Worth every penny.",
                rating: 5
              },
              { 
                name: "Sarah T.", 
                business: "Thompson Painting Co.", 
                text: "The Local Ad Boost was exactly what I needed. Got real leads within the first week. Professional ads without the agency prices!",
                rating: 5
              },
              { 
                name: "Carlos M.", 
                business: "CM Electric", 
                text: "Fast turnaround on yard signs and door hangers. Clean designs that look way better than what I had before.",
                rating: 5
              }
            ].map((review, index) => (
              <motion.div
                key={review.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
                whileHover={{ y: -5 }}
                className="bg-slate-50 rounded-2xl p-6 border border-gray-100"
              >
                <div className="flex gap-1 mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4 leading-relaxed">"{review.text}"</p>
                <div>
                  <p className="font-semibold text-gray-900">{review.name}</p>
                  <p className="text-sm text-gray-500">{review.business}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-[#1E3A8A] to-[#3B82F6] relative overflow-hidden">
        {/* Animated background shapes */}
        <motion.div
          className="absolute top-0 left-0 w-64 h-64 bg-white/10 rounded-full -translate-x-1/2 -translate-y-1/2"
          animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
          transition={{ duration: 10, repeat: Infinity }}
        />
        <motion.div
          className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full translate-x-1/2 translate-y-1/2"
          animate={{ scale: [1, 1.3, 1] }}
          transition={{ duration: 8, repeat: Infinity }}
        />

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to look professional?
            </h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Join hundreds of local businesses who launched their marketing the smart way — 
              one project at a time.
            </p>
            <Link to="/request">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="inline-block"
              >
                <Button className="bg-white text-[#1E3A8A] hover:bg-gray-100 text-lg px-10 py-6 rounded-full shadow-2xl font-semibold">
                  Start Your Project Today
                  <Sparkles className="w-5 h-5 ml-2" />
                </Button>
              </motion.div>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-12">
            <div className="md:col-span-2">
              <h3 className="text-2xl font-bold bg-gradient-to-r from-white to-blue-200 bg-clip-text text-transparent mb-4">
                BM-Assist.co
              </h3>
              <p className="text-gray-400 mb-6 max-w-md">
                Your marketing launchpad. We help local businesses get started with professional
                marketing materials — one project at a time.
              </p>
              <div className="flex gap-4">
                {industries.map((ind) => (
                  <div key={ind.label} className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                    <ind.icon className="w-5 h-5 text-white" />
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-white">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/mini-services" className="text-gray-400 hover:text-white transition-colors">Marketplace</Link></li>
                <li><Link to="/team" className="text-gray-400 hover:text-white transition-colors">Our Team</Link></li>
                <li><Link to="/applications" className="text-gray-400 hover:text-white transition-colors">Join Our Team</Link></li>
                <li><Link to="/request" className="text-gray-400 hover:text-white transition-colors">Start a Project</Link></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4 text-white">Legal</h4>
              <ul className="space-y-2">
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="text-gray-400 hover:text-white transition-colors">Terms of Service</Link></li>
                <li><Link to="/cookies" className="text-gray-400 hover:text-white transition-colors">Cookie Policy</Link></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500">
            <p>© {new Date().getFullYear()} BM-Assist.co. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
